import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../../config";
import GenericTable from "../GenericTable/GenericTable";
import { CircularProgress, Box, TextField, Button, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio } from "@mui/material";

const formatValue = (value) =>
  value === null || value === undefined || value === "" ? "N/A" : value;

// ✅ Format date from ISO format (2025-12-02T00:00:00) to MM-DD-YYYY HH:MM:SS
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return "N/A";
  }
};

const ChatColumns = [
  { id: "client_name", label: "Client Name", render: (row) => formatValue(row.client_name) },
  { id: "customer_name", label: "Customer Name", render: (row) => formatValue(row.customer_name) },
  { id: "store_name", label: "Store Name", render: (row) => formatValue(row.store_name) },
  { id: "last_visit", label: "Last Visit", render: (row) => formatDate(row.last_visit) },
  {
    id: "top_mood",
    label: "Top Mood",
    render: (row) => {
      if (Array.isArray(row.top_mood)) {
        return row.top_mood.join(", ");
      }
      return formatValue(row.top_mood);
    }
  },
  { id: "active_time", label: "Active Time", render: (row) => formatValue(row.active_time) },
  { id: "chat_overview", label: "Chat Overview", render: (row) => formatValue(row.chat_overview) }
];

function ChatTranscriptsList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dropdown options
  const [clients, setClients] = useState([]);
  const [stores, setStores] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  // Export states
  const [exportOpen, setExportOpen] = useState(false);
  const [exportType, setExportType] = useState("csv");



  // ✅ UNIVERSAL FETCH: Server-side pagination
  const fetchChatTranscripts = useCallback(async (pageNum = page, limitNum = limit) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = {
        page: pageNum,
        limit: limitNum,
      };

      const response = await axios.get(
        `${config.API_BASE_URL_V1}/admin/chat-transcripts/list`,
        { params: queryParams }
      );

      console.log("Chat transcripts response:", response.data);

      if (response.data.success && response.data.data && response.data.data.chat_sessions) {
        setRows(response.data.data.chat_sessions);
        setTotalCount(response.data?.pagination?.total_records || 0);
      } else {
        setError(response.data.message || "Failed to fetch chat transcripts");
        setRows([]);
      }
    } catch (err) {
      console.error("Error fetching chat transcripts:", err);
      setError(`Error loading chat transcripts: ${err.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  // ✅ AUTO-FETCH: When page or limit changes
  useEffect(() => {
    fetchChatTranscripts(page, limit);
  }, [page, limit, fetchChatTranscripts]);



  // ✅ EXPORT: Get ALL results (ignores pagination)
  const handleExportData = async () => {
    try {
      setLoading(true);
      let allData = [];
      let currentPage = 1;
      let hasMore = true;

      // ✅ Fetch ALL pages until no more data
      while (hasMore) {
        const res = await axios.get(`${config.API_BASE_URL_V1}/admin/chat-transcripts/list`, {
          params: {
            page: currentPage,
            limit: 100, // Backend max limit is 100
          },
        });

        const pageData = res.data?.data?.chat_sessions || [];

        if (pageData.length === 0) {
          hasMore = false;
        } else {
          allData = [...allData, ...pageData];
          currentPage++;
        }
      }

      // Generate CSV
      const headers = [
        "Client Name",
        "Customer Name",
        "Store Name",
        "Last Visit",
        "Top Mood",
        "Active Time",
      ];

      const csvContent = [
        headers.join(","),
        ...allData.map((row) =>
          [
            row.client_name,
            row.customer_name,
            row.store_name,
            formatDate(row.last_visit),
            Array.isArray(row.top_mood) ? row.top_mood.join("; ") : row.top_mood || "",
            row.active_time || "N/A",
          ].join(",")
        ),
      ].join("\n");

      // Download CSV
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `chat_transcripts_export_${new Date().getTime()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setExportOpen(false);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <Box p={2} color="error.main">
        {error}
      </Box>
    );
  }

  return (
    <div>
      {/* ✅ ACTION BAR */}
      <Box sx={{ mb: 0, p: 2, backgroundColor: "#ffffff", borderRadius: "8px 8px 0 0", display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", border: "1px solid #e0e0e0", borderBottom: "none" }}>
        <Button
          variant="contained"
          onClick={() => setExportOpen(true)}
          sx={{ height: "40px", backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } }}
        >
          Export
        </Button>
      </Box>

      {/* ✅ EXPORT DIALOG */}
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)}>
        <DialogTitle>Export Chat Transcripts</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={exportType}
            onChange={(e) => setExportType(e.target.value)}
            sx={{ marginTop: "16px" }}
          >
            <FormControlLabel value="csv" control={<Radio />} label="CSV" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)}>Cancel</Button>
          <Button onClick={handleExportData} variant="contained">
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ TABLE WITH SERVER-SIDE PAGINATION */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <GenericTable
          title=""
          columns={ChatColumns}
          rows={rows}
          showView={true}
          showEdit={false}
          showDelete={false}
          hideExport={true}
          isServerSide={true}
          currentPage={page - 1}
          rowsPerPage={limit}
          totalCount={totalCount}
          onPageChange={(_, newPage) => {
            setPage(newPage + 1);
          }}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setLimit(newRowsPerPage);
            setPage(1);
          }}
          onView={(row) => {
            navigate(`/chat-transcripts/chats/${row.store_customer_id}`);
          }}
        />
      )}
    </div>
  );
}

export default ChatTranscriptsList;
